/**
 * Central configuration for organisation identity, contact routes and navigation.
 *
 * FACT POLICY
 * -----------
 * Values marked `confirmed: true` are published on the organisation's own website
 * or LinkedIn profile (see source comments). Values marked `confirmed: false`
 * conflict between sources or are not published at all, they are rendered with a
 * neutral fallback and listed in CONTENT_CONFIRMATION.md for client sign-off.
 * Never promote a value to `confirmed: true` without a client-approved source.
 */

export type Confirmable<T> = {
  value: T;
  confirmed: boolean;
  note?: string;
};

/**
 * Public origin. Defined once in lib/site-url.ts and re-exported here so the
 * many modules that already import it from this file keep working.
 */
export { siteUrl } from "@/lib/site-url";

import { organisationSettings } from "@/data/organisation";

/**
 * Organisation identity.
 *
 * Assembled from data/organisation.ts, which is the snapshot that seeds the
 * editable OrganizationSettings document. One definition, two consumers: the
 * seed and the components that render before the settings arrive.
 */
export const org = {
  legalName: organisationSettings.legalName,
  shortName: organisationSettings.shortName,
  initials: organisationSettings.initials,
  // Source: https://himshikharanepal.org.np/homepage/about-us/
  establishedYear: organisationSettings.establishedYear,
  positioning: organisationSettings.positioning,
  motto: organisationSettings.motto,
  tagline: organisationSettings.tagline,
  // Source: official About page
  vision: organisationSettings.vision,
  mission: organisationSettings.mission,
  registrations: organisationSettings.registrations.map((r) => ({ label: r.label, value: r.value })),
} as const;

/**
 * Contact details.
 *
 * These now come from the CMS at run time. The values here are the seeded
 * defaults and the fallback for a render that happens before the settings
 * document exists, which is why they are still source-commented. The
 * authoritative copy, and the one staff edit, is organisation.ts and the
 * OrganizationSettings document it seeds.
 */
export const contact = {
  // Source: https://himshikharanepal.org.np/homepage/contact-us/
  address: {
    value: organisationSettings.address.value,
    confirmed: organisationSettings.address.confirmed,
    note: organisationSettings.address.note,
  } satisfies Confirmable<string>,
  phone: {
    value: organisationSettings.phone.value,
    confirmed: organisationSettings.phone.confirmed,
    note: organisationSettings.phone.note,
  } satisfies Confirmable<string>,
  email: {
    value: organisationSettings.email.value,
    confirmed: organisationSettings.email.confirmed,
    note: organisationSettings.email.note,
  } satisfies Confirmable<string>,
  mapEmbedUrl: {
    value: organisationSettings.mapEmbedUrl.value,
    confirmed: organisationSettings.mapEmbedUrl.confirmed,
    note: organisationSettings.mapEmbedUrl.note,
  } satisfies Confirmable<string>,
} as const;

export const socialLinks = organisationSettings.socialLinks.map((s) => ({
  label: s.label,
  href: s.href,
}));

/**
 * Navigation moved to data/navigation.ts when it became editable.
 *
 * Re-exported here because a dozen modules already import it from this file and
 * there is nothing to gain from changing all of them. The header and footer
 * read the live copy from the CMS and fall back to these.
 */
export type { NavItem } from "@/data/navigation";
export { primaryNav, footerNav } from "@/data/navigation";
